'use client'

// React Imports
import { useState } from 'react'

// MUI Imports
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import Grid from '@mui/material/Grid'

// Third-party Imports
import { TVStorApiError } from 'trantor-tvstor-api-js'

// Component Imports
import PasswordField from '@components/user/PasswordField'

// Hook Imports
import { useTranslation } from '@/i18n'
import { shakeFrames, useDialogShake } from '@components/useDialogShake'

// The mustChangePassword gate at login (contract §users): while the flag is set every
// endpoint answers 403 except the session ones and the self password change — so the
// login flow must force the change BEFORE entering the dashboard. Cancel aborts the
// login (no cookie was written yet).
//
// Exit-animation sweep (Luca 2026-07-24): the parent mounts on demand via
// useExitTransition — open drives the fade, onExited signals the unmount.
const ForcePasswordChangeDialog = ({
  open,
  onExited,
  onSubmit,
  onCancel
}: {
  open: boolean
  onExited: () => void
  onSubmit: (newPassword: string) => Promise<void>
  onCancel: () => void
}) => {
  // States
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Hooks
  const { t } = useTranslation()
  const { shaking, shake } = useDialogShake()

  const mismatch = confirm !== '' && next !== confirm
  const valid = next !== '' && !mismatch

  const submit = async () => {
    setSubmitting(true)
    setError(null)

    try {
      await onSubmit(next)
    } catch (err) {
      setError(err instanceof TVStorApiError ? err.message : t('user.password.error'))
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth='xs'
      slotProps={{
        paper: { sx: shaking ? { animation: `${shakeFrames} 0.35s` } : undefined },
        transition: { onExited }
      }}

      // The gate already refused backdrop/Esc; the shake makes the refusal
      // visible (dialog hygiene sweep, Luca 2026-07-22). Cancel aborts the login.
      onClose={shake}
    >
      <DialogTitle>{t('user.password.forcedTitle')}</DialogTitle>
      <DialogContent>
        <Alert severity='info' className='mbe-4'>
          {t('user.password.forcedHint')}
        </Alert>
        {error !== null && (
          <Alert severity='error' className='mbe-4'>
            {error}
          </Alert>
        )}
        <Grid container spacing={5}>
          <Grid size={{ xs: 12 }}>
            <PasswordField
              label={t('user.password.new')}
              value={next}
              onChange={setNext}
              ariaLabel='forced-new-password'
              autoComplete='new-password'
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <PasswordField
              label={t('user.password.confirm')}
              value={confirm}
              onChange={setConfirm}
              ariaLabel='forced-confirm-password'
              autoComplete='new-password'
              error={mismatch}
              helperText={mismatch ? t('user.password.mismatch') : undefined}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant='tonal' color='secondary' onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button variant='contained' disabled={!valid || submitting} onClick={submit}>
          {t('user.password.forcedSubmit')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ForcePasswordChangeDialog
